import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import PageTitle from '../../FormElements/PageTitle';
import { useAuth } from '../../../contexts/AuthContext';
import useInjuryReport from '../../../hooks/useInjuryReport';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';
import InjuryReportSummaryBar from '../../InjuryReport/InjuryReportSummaryBar';
import InjuryReportFilters from '../../InjuryReport/InjuryReportFilters';
import InjuryReportTable from '../../InjuryReport/InjuryReportTable';
import InjuryReportMobileCard from '../../InjuryReport/InjuryReportMobileCard';
import { injuryReportPanelClassName } from '../../InjuryReport/injuryReportStyles';

function useInitialScope() {
	const { search } = useLocation();
	const params = new URLSearchParams(search);
	return params.get('scope') === 'mine' ? 'mine' : 'all';
}

const SORT_LABELS = {
	impact: 'fantasy impact',
	severity: 'severity',
	rank: 'FF rank',
	name: 'name'
};

export default function InjuryReportPage() {
	const { user, csrfToken, endpoints } = useAuth();
	const initialScope = useInitialScope();
	const endpointTemplate = endpoints?.get_injury_report_by_user_endpoint;

	const {
		meta,
		players,
		loading,
		error,
		filters,
		updateFilter,
		setScope
	} = useInjuryReport({
		user,
		endpointTemplate,
		csrfToken,
		initialScope
	});

	const teams = useMemo(() => {
		const names = new Set();
		players.forEach((player) => {
			if (player.team?.name) names.add(player.team.name);
		});
		return Array.from(names).sort();
	}, [players]);

	const emptyMessage =
		filters.scope === 'mine'
			? 'No injured players on your rosters match these filters.'
			: 'No injured players match these filters.';

	return (
		<div className="container p-4 pt-4 lg:py-14 md:px-20">
			<div className="mt-4 space-y-6">
				<PageTitle title="Injury Report" />

				<InjuryReportSummaryBar
					meta={meta}
					loading={loading}
					scope={filters.scope}
					onScopeChange={setScope}
				/>

				<InjuryReportFilters
					filters={filters}
					onFilterChange={updateFilter}
					teams={teams}
				/>

				{error && (
					<div
						className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-900 dark:bg-opacity-30 dark:text-red-300"
						role="alert"
					>
						{error}
					</div>
				)}

				{loading && players.length === 0 ? (
					<div className={`flex justify-center py-20 ${injuryReportPanelClassName}`}>
						<LoadingSpinner />
					</div>
				) : players.length === 0 ? (
					<div className={`px-8 py-16 text-center ${injuryReportPanelClassName}`}>
						<p className="text-lg font-medium text-gray-700 dark:text-dark-text">
							All clear
						</p>
						<p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{emptyMessage}</p>
					</div>
				) : (
					<div className="space-y-3">
						<p className="text-xs font-medium text-gray-500 dark:text-gray-400 px-1">
							<span className="font-semibold text-gray-800 dark:text-dark-text">
								{players.length}
							</span>{' '}
							players · sorted by {SORT_LABELS[filters.sort] || filters.sort}
						</p>
						<InjuryReportTable players={players} />
						<div className="space-y-3 lg:hidden">
							{players.map((player) => (
								<InjuryReportMobileCard
									key={`${player.position}-${player.id}`}
									player={player}
								/>
							))}
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
